import { toast } from "sonner";

// Event categories (pick 1-3). Keys must match EVENT_CATEGORIES on the backend.
export const EVENT_CATEGORIES = [
  { key: "self-development", label: "Self-development", emoji: "📚", cls: "bg-[#8C9A7C]/20 text-[#4F5C42]" },
  { key: "business", label: "Business", emoji: "💼", cls: "bg-[#2E2A26]/10 text-[#2E2A26]" },
  { key: "connection", label: "Connection", emoji: "🤝", cls: "bg-[#C85A38]/15 text-[#A84830]" },
  { key: "community", label: "Community", emoji: "🏡", cls: "bg-[#E8DAC8] text-[#6B5A48]" },
  { key: "wellness", label: "Wellness", emoji: "🧘", cls: "bg-[#D9E4DC] text-[#3F5A4A]" },
  { key: "sport", label: "Sport", emoji: "🏃", cls: "bg-[#DDE8F3] text-[#2F4B66]" },
  { key: "outdoors", label: "Outdoors", emoji: "🥾", cls: "bg-[#E3EBD9] text-[#4A5E33]" },
  { key: "food", label: "Food", emoji: "🍽️", cls: "bg-[#F4E3C1] text-[#7A5A1E]" },
  { key: "creative", label: "Creative", emoji: "🎨", cls: "bg-[#EBD9EC] text-[#6A3F6E]" },
  { key: "fun", label: "Fun", emoji: "🎉", cls: "bg-[#FBE3D6] text-[#9A4A2A]" },
];

export const catMeta = (key) => EVENT_CATEGORIES.find((c) => c.key === key);
export const emojiForCategories = (cats) => catMeta((cats || [])[0])?.emoji || "📅";

export const CategoryTags = ({ categories, size = "xs", testid }) => {
  const cats = (categories || []).map(catMeta).filter(Boolean);
  if (cats.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${size === "xs" ? "mt-1" : "mt-2"}`} data-testid={testid}>
      {cats.map((c) => (
        <span key={c.key} className={`${c.cls} rounded-full font-medium ${size === "xs" ? "text-[9px] px-1.5 py-px" : "text-[11px] px-2 py-0.5"}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
};

// Toggleable category chips used by the guest and admin event forms.
export const CategoryPicker = ({ value = [], onChange, max = 3, testidPrefix = "add-event-category" }) => {
  const toggle = (key) => {
    const has = value.includes(key);
    if (!has && value.length >= max) { toast.error(`Up to ${max} categories per event`); return; }
    onChange(has ? value.filter((c) => c !== key) : [...value, key]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-list`}>
      {EVENT_CATEGORIES.map((c) => {
        const on = value.includes(c.key);
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => toggle(c.key)}
            aria-pressed={on}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
              on ? "bg-[#2E2A26] border-[#2E2A26] text-white" : `${c.cls} border-transparent hover:border-[#2E2A26]/30`
            }`}
            data-testid={`${testidPrefix}-${c.key}`}
          >
            {c.emoji} {c.label}
          </button>
        );
      })}
    </div>
  );
};

export const EVENT_EMOJI_OPTIONS = [...new Set([...EVENT_CATEGORIES.map((c) => c.emoji), "🎉", "☕", "🎬", "🎲", "🎶", "🔥", "🌅", "🧘"])];

// Row of tappable emoji chips; the first category pre-selects one, tapping overrides it.
export const EmojiPicker = ({ value, onChange, testidPrefix = "add-event-emoji" }) => (
  <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5" data-testid={testidPrefix}>
    {EVENT_EMOJI_OPTIONS.map((em) => (
      <button
        key={em}
        type="button"
        onClick={() => onChange(em)}
        className={`w-10 h-10 rounded-lg text-xl flex-shrink-0 border transition-colors ${value === em ? "bg-[#C85A38]/10 border-[#C85A38]" : "bg-white border-stone-200"}`}
        aria-label={`Icon ${em}`}
        aria-pressed={value === em}
        data-testid={`${testidPrefix}-${em}`}
      >
        {em}
      </button>
    ))}
  </div>
);
